// PATH: components/ide-console/IDEConsoleAdmin.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Col, Row, message, Button } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { UnifiedIDE } from "../IDE/ide";
import { getIDEConfigDefault, LanguageId } from "../IDE/ide/core/types";
import Console from "../Console";
import IDEConsoleConfigEditor from "./IDEConsoleConfigEditor";
import type { IDEConsoleConfig } from "./types";
import type { IDEConsoleAdminProps } from "./types";
import { createDefaultConfig } from "./utils/config";
import { validateAndSanitizeRules } from "./utils/validationRules";
import type { ConsoleOutput } from "../Console/types";
import { useCourseApi } from "../../api/courses/courses";
import type { TLesson } from "../../models/courses/lesson";
import { IDEConsoleBridge } from "./utils/executionBridge";
import ResponsiveConsoleIDELayout from "./ResponsiveConsoleIDELayout";
import "./styles.css";

const IDEConsoleAdmin: React.FC<IDEConsoleAdminProps> = ({
  editingLesson,
  language,
  onSave,
  onCodeChange,
}) => {
  const [config, setConfig] = useState<IDEConsoleConfig | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState<ConsoleOutput[]>([]);
  // 👉 on garde le dernier code saisi par l'IDE pour le persist
  const [latestCode, setLatestCode] = useState<string>('');
  const [importedStatement, setImportedStatement] = useState<string>('');

  const { saveLessonDetails } = useCourseApi();
  const bridgeRef = useRef<IDEConsoleBridge | null>(null);

  // Initialize execution bridge
  const initializeBridge = useCallback(() => {
    if (!bridgeRef.current) {
      bridgeRef.current = new IDEConsoleBridge((outputs) => {
        setConsoleOutput(outputs);
      });
    }
    return bridgeRef.current;
  }, []);

  const handleConsoleLog = useCallback(
    (message: string) => {
      const bridge = initializeBridge();
      bridge.onLog(message);
    },
    [initializeBridge]
  );

  // Initialisation: config (following IDEPhaser pattern)
  useEffect(() => {
    if (!editingLesson || !editingLesson.config || !editingLesson.config.config) {
      const init = {
        language,
        initialCode: '',
        validationRules: createDefaultConfig(language).validationRules,
      };
      setConfig(init as IDEConsoleConfig);
      // initialise latestCode avec le code par défaut
      setLatestCode(init.initialCode || '');
      setImportedStatement('');
    } else {
      const cfg = editingLesson.config.config as IDEConsoleConfig;
      setConfig(cfg);
      setLatestCode(cfg?.initialCode || '');
      // Initialize imported statement from lesson
      setImportedStatement(editingLesson.statement || '');
    }
  }, [editingLesson, language]);

  // Initialize bridge on mount
  useEffect(() => {
    initializeBridge();
  }, [initializeBridge]);

  // Configuration change handler
  const handleConfigChange = (incoming: Partial<IDEConsoleConfig>) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const next: IDEConsoleConfig = {
        ...prev,
        ...incoming,
        // s'assurer que les règles sont propres
        validationRules: validateAndSanitizeRules(
          (incoming.validationRules ?? prev.validationRules) as any[]
        ),
        // toujours forcer le language courant de l'onglet
        language: prev.language,
      };
      return next;
    });
    message.info('Configuration mise à jour');
  };

  // Save function (following IDEPhaser pattern)
  const save = async (): Promise<void> => {
    if (!editingLesson || !config) return;

    // 🔐 on reconstruit la config IDE en gardant tout l'existant mais avec le code courant
    const configurationMirror = {
      language: config.language,
      initialCode: latestCode, // <- code saisi dans le nouvel IDE
      validationRules: config.validationRules,
    };

    try {
      const data: TLesson = {
        ...editingLesson,
        // Prioritize imported statement over existing lesson statement
        statement: importedStatement || editingLesson.statement || '',
        config: {
          id: 0,
          lesson_id: editingLesson.id,
          config: configurationMirror as IDEConsoleConfig,
        },
      };
      await saveLessonDetails(data);
      message.success('Leçon sauvegardée.');
    } catch (err: any) {
      message.error(err.response?.data ?? err.message ?? err);
    }
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Configuration Editor Button */}
      {config && (
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <IDEConsoleConfigEditor
            currentConfig={config}
            onConfigChange={handleConfigChange}
            title={`${language.charAt(0).toUpperCase()}${language.slice(1)} Console — Config`}
            storageKey={`ideconsole-admin-config-${language}`}
            initialStatement={importedStatement}
            onStatementChange={setImportedStatement}
          />
        </div>
      )}

      {config && !loading && (
        <ResponsiveConsoleIDELayout
          ideComponent={
            <div className="ideconsole-hide-help" style={{ height: '100%' }}>
              <UnifiedIDE
                language={language}
                initialCode={config.initialCode || ''}
                projectMode={false}
                consoleMode={true}
                adminMode={false}
                sprites={[]}
                scenesConfig={[]}
                obstacles={[]}
                sounds={[]}
                helpConfig={{}}
                helpGrouped={false}
                bridge={bridgeRef.current!}
                onStop={async () => {
                  // Console stop handler
                }}
                onRestoreInitial={() => {
                  setLatestCode(config.initialCode || '');
                }}
                onCodeChange={(code: string) => setLatestCode(code)}
                onLog={handleConsoleLog}
              />
            </div>
          }
          consoleComponent={
            <Console
              output={consoleOutput}
              height={400}
              theme="dark"
              onClear={() => {
                setConsoleOutput([]);
                bridgeRef.current?.clear();
              }}
            />
          }
          style={{ height: 'calc(100vh - 200px)' }}
        />
      )}

      <Button 
        type="default" 
        shape="circle"
        icon={<SaveOutlined />} 
        onClick={save}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          color: '#1890ff'
        }}
      />
    </div>
  );
};

export default IDEConsoleAdmin;
